import { useEffect, useMemo } from 'react'

import { usePagination } from '@webapp/core/hooks/usePagination'
import {
  type PaginationID,
  type PaginationParams,
} from '@webapp/core/providers/Pagination/pagination.types'
import { type LegalEntity } from '@webapp/platform/legal-entities/entities/LegalEntity'
import { useQueryLegalEntities } from '@webapp/platform/legal-entities/hooks/useQueryLegalEntities'

export const LEGAL_ENTITY_LIST_PAGINATION_ID: PaginationID = 'legal-entity-list'

/**
 * Sorts entities by the pagination `orderBy` token. Supports `'name'` /
 * `'-name'` today (matching `pagination.config.ts` default + the descending
 * convention used elsewhere). Unknown tokens fall through unchanged, so any
 * future Sort UI can extend this without breaking the current default.
 */
const sortEntities = (
  entities: LegalEntity[],
  orderBy: string | undefined
): LegalEntity[] => {
  if (!orderBy) return entities
  const descending = orderBy.startsWith('-')
  const field = descending ? orderBy.slice(1) : orderBy

  if (field === 'name') {
    return [...entities].sort((a, b) => {
      const cmp = a.name.localeCompare(b.name, undefined, {
        sensitivity: 'base',
      })
      return descending ? -cmp : cmp
    })
  }

  return entities
}

export interface IUseLegalEntityListPresenterParams {
  isActive: boolean
}

/**
 * Keeps the shared `usePagination` state in sync with our client-side
 * filtering. Three concerns, kept as three separate effects so each fires
 * only on the trigger that should re-run it (combining them would cause
 * spurious `updateNumPages` writes on every `page` change → loops):
 *
 * 1. Tab change → reset `page` to 1. In `local` pagination mode the URL's
 *    `?page=1` on tab links is ignored, so we reset explicitly.
 * 2. `numPages` change → mirror into the provider so
 *    `PaginationControls.Page` can render "Page X of N" correctly.
 * 3. `page > numPages` (e.g., background refetch shrinks the result set) →
 *    clamp `page` back into bounds, otherwise the pagination chrome shows
 *    "Page 5 of 2" while the slice silently uses the lower page.
 *
 * The `eslint-disable react-hooks/exhaustive-deps` comments are intentional:
 * `updateParams` and `updateNumPages` are recreated on every provider state
 * change, so depending on them would cause every effect to re-fire on
 * every render and loop.
 */
const usePaginationStateSync = ({
  isActive,
  page,
  numPages,
  updateParams,
  updateNumPages,
}: {
  isActive: boolean
  page: number
  numPages: number
  updateParams: (params: Partial<PaginationParams>) => void
  updateNumPages: (numPages: number) => void
}) => {
  // 1. Tab change → reset page.
  useEffect(() => {
    updateParams({ page: 1 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive])

  // 2. Mirror locally-derived `numPages` into the provider.
  useEffect(() => {
    updateNumPages(numPages)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numPages])

  // 3. Clamp `page` when `numPages` shrinks below it.
  useEffect(() => {
    if (page > numPages) {
      updateParams({ page: numPages })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, numPages])
}

/**
 * Pulls the LegalEntity list, filters to the requested tab + search term
 * from `usePagination`, slices to the current page, and exposes shaped
 * data / states / commands for the View.
 *
 * The list endpoint is not server-paginated (returns the full array), so
 * filtering and slicing happen client-side off the shared pagination state.
 *
 * NOTE: There is a fair amount of glue between this presenter and the
 * `PaginationControls` provider — `usePaginationStateSync`, `safePage`,
 * and the manual `params` reads exist because we're reusing a server-side
 * pagination primitive for client-side filtering. The second client-side
 * consumer (`iam/features/list-user-legal-entities`, PLAT-5857) deliberately
 * kept plain `useState` page/pageSize instead of this provider. Extract a
 * shared `useClientSidePagination(...)` hook when a consumer needs both the
 * URL-backed params and client-side slicing, designing it against both
 * examples.
 */
export const useLegalEntityListPresenter = ({
  isActive,
}: IUseLegalEntityListPresenterParams) => {
  const {
    entities,
    isLoading,
    isError: hasQueryError,
  } = useQueryLegalEntities()
  const { params, updateNumPages, updateParams } = usePagination({
    id: LEGAL_ENTITY_LIST_PAGINATION_ID,
  })

  const search = (params.search ?? '').trim().toLowerCase()
  const { pageSize, page, orderBy } = params

  const tabEntities = useMemo(
    () => entities.filter((entity) => entity.active === isActive),
    [entities, isActive]
  )

  const filteredEntities = useMemo(() => {
    const matched = search
      ? tabEntities.filter(
          (entity) =>
            entity.name.toLowerCase().includes(search) ||
            entity.externalId.toLowerCase().includes(search)
        )
      : tabEntities
    return sortEntities(matched, orderBy)
  }, [tabEntities, search, orderBy])

  const numPages = Math.max(1, Math.ceil(filteredEntities.length / pageSize))

  usePaginationStateSync({
    isActive,
    page,
    numPages,
    updateParams,
    updateNumPages,
  })

  // Clamp the slice to the current numPages too — covers the single frame
  // between the clamp effect detecting an out-of-bounds page and the
  // updated `params.page` propagating.
  const safePage = Math.min(page, numPages)
  const paginatedEntities = useMemo(
    () =>
      filteredEntities.slice((safePage - 1) * pageSize, safePage * pageSize),
    [filteredEntities, safePage, pageSize]
  )

  // First-run is only valid when we successfully fetched and got zero rows.
  // On error, `entities` is also `[]` (data falls back to []), so without the
  // `!hasQueryError` guard the View would mistake a failed load for
  // "no entities".
  const isFirstRunEmpty = !isLoading && !hasQueryError && entities.length === 0

  const hasLoadFailed = hasQueryError && entities.length === 0

  return {
    data: {
      entities: paginatedEntities,
      totalCount: filteredEntities.length,
      tabCount: tabEntities.length,
      hasSearch: search.length > 0,
      isFirstRunEmpty,
    },
    states: {
      isLoading,
      hasLoadFailed,
    },
  }
}
